import SettingsIcon from "@mui/icons-material/Settings";
import parse from "html-react-parser";
import React, { useState } from "react";

import Button from "~/components/parts/Button";
import { RestrictedFancyLink } from "~/components/parts/FancyLink";
import Modal from "~/components/parts/Modal";
import Spinner from "~/components/parts/Spinner";

import { allocateCourseToSchool, cloneCourse } from "~/utils/courses";
import { buildErrorMessageHtml } from "~/utils/errors";
import {
  posthogEventCourseAdded,
  posthogEventCourseCopied,
  posthogEventCourseManageClicked,
} from "~/utils/posthog/courses";

export default function CourseManageOptions({
  buildPosthogTriggerId,
  course,
  courseAdded,
  onCourseAdd,
  user = {},
}) {
  const { sid, type: userType, _school: school = {} } = user;
  const { name: schoolName } = school;

  const isOwnSchoolCourse = course.sid === sid;

  const [showAdaptEntitySelect, setShowAdaptEntitySelect] = useState(false);
  const [showAdaptLoading, setShowAdaptLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAddCourse = async () => {
    try {
      await allocateCourseToSchool(course.coid, sid);

      const options = {
        coid: course.coid,
        displayMessage: "Just added",
        focusOnCourse: true,
        scrollIntoView: true,
      };
      sessionStorage.setItem(
        "manageCourseDisplayOptions",
        JSON.stringify(options),
      );

      if (buildPosthogTriggerId) {
        posthogEventCourseAdded({
          triggerId: buildPosthogTriggerId("add-to-school"),
          course,
        });
      }

      onCourseAdd();
    } catch (e) {
      setError(
        "There was an error adding this course, please try again later.",
      );
    }
  };

  const handleAdaptCourse = async (entity = "school") => {
    try {
      setShowAdaptLoading(true);

      const newCoid = await cloneCourse(course.coid, entity);

      const options = {
        coid: newCoid,
        displayMessage: "Just copied",
        scrollIntoView: true,
      };
      sessionStorage.setItem(
        "manageCourseDisplayOptions",
        JSON.stringify(options),
      );

      sessionStorage.setItem("manageCoursesView", entity);

      if (buildPosthogTriggerId) {
        posthogEventCourseCopied({
          triggerId: buildPosthogTriggerId("copy-and-adapt"),
          course,
          copiedCourseId: newCoid,
          entityType: entity,
        });
      }

      // Allow the loading message to show for a moment before redirecting.
      setTimeout(() => {
        setShowAdaptLoading(false);
        window.location = `/edit-course.php?coid=${newCoid}`;
      }, 2000);
    } catch (e) {
      setShowAdaptLoading(false);
      setError(
        e.message === "TIMEOUT"
          ? "Copying this course is taking a long time. Please check your 'Draft' section in a few minutes for your completed copy."
          : "There was an error copying this course. Please try again later.",
      );
    }
  };

  const manageHref = "/manage-courses.php";
  const handleManageClick = (e) => {
    e.preventDefault();

    const options = {
      coid: course.coid,
      focusOnCourse: true,
      scrollIntoView: true,
    };
    sessionStorage.setItem(
      "manageCourseDisplayOptions",
      JSON.stringify(options),
    );

    posthogEventCourseManageClicked({
      triggerId: buildPosthogTriggerId("manage-course"),
      course,
    });

    window.location = manageHref;
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {!courseAdded && !isOwnSchoolCourse && (
          <>
            <RestrictedFancyLink
              className="grow"
              message="Your school needs an active subscription to add courses"
              onClick={(e) => {
                e.preventDefault();
                handleAddCourse();
              }}
              size="sm"
            >
              Add to school
            </RestrictedFancyLink>

            <RestrictedFancyLink
              className="grow"
              message="Your school needs an active subscription to copy and adapt courses"
              onClick={(e) => {
                e.preventDefault();
                if (userType === "trust") {
                  setShowAdaptEntitySelect(true);
                } else {
                  handleAdaptCourse("school");
                }
              }}
              size="sm"
            >
              Copy and adapt
            </RestrictedFancyLink>
          </>
        )}

        {courseAdded && (
          <RestrictedFancyLink
            className="grow"
            message="Your school needs an active subscription to manage courses"
            onClick={handleManageClick}
            href={manageHref}
            size="sm"
          >
            <div className="flex flex-none justify-center transition-transform duration-500 group-hover:rotate-180">
              <SettingsIcon sx={{ fontSize: "18px" }} />
            </div>
            <span>Manage this course</span>
          </RestrictedFancyLink>
        )}
      </div>

      <Modal
        open={showAdaptEntitySelect}
        className="h-auto"
        closeModal={() => setShowAdaptEntitySelect(false)}
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-lg font-light text-slate-500">
            Copy and adapt
          </p>
          <div className="text-sm text-slate-700">
            Would you like to copy and adapt this course just for this school (
            {schoolName}) or for your trust?
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              className="w-[100px] bg-blue-500 p-1.5 text-sm text-white shadow-sm hover:bg-blue-500"
              onClick={() => {
                handleAdaptCourse("school");
                setShowAdaptEntitySelect(false);
              }}
            >
              School
            </Button>
            <Button
              className="w-[100px] bg-blue-500 p-1.5 text-sm text-white shadow-sm hover:bg-blue-500"
              onClick={() => {
                handleAdaptCourse("trust");
                setShowAdaptEntitySelect(false);
              }}
            >
              Trust
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={showAdaptLoading}
        className="h-auto"
        closeModal={() => setShowAdaptLoading(false)}
        dismissible={false}
      >
        <div className="flex flex-col items-center gap-4">
          <p className="m-0 text-sm text-gray-400">
            Creating a copy of the course...
          </p>
          <Spinner className="mb-1 h-8 w-8" />
        </div>
      </Modal>

      <Modal
        className="h-auto"
        closeModal={() => setError(null)}
        open={!!error}
      >
        <div className="m-3">{parse(buildErrorMessageHtml(error))}</div>
      </Modal>
    </>
  );
}
